/**
 * Générateur — Additionner et soustraire des fractions
 * Attendu ID : 630 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 9 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 13 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER17 §6.1 à §6.9) :
 *
 *   1. `somme_meme_denominateur`          → QCM, Saisie, V/F. Couple (a/b, c/b).
 *   2. `somme_meme_denominateur_guidee`   → Texte à trous saisie. Même couple exposé en étapes.
 *   3. `somme_denominateurs_multiples`    → QCM, Saisie. Couple (a/b, c/d) avec d = k·b.
 *   4. `somme_denominateurs_multiples_guidee` → Texte à trous saisie. Idem exposé en étapes.
 *   5. `entier_plus_ou_moins_fraction`    → QCM, Saisie. Couple (n, a/b).
 *   6. `enchainement_trois_operandes`     → Saisie. Trois numérateurs partageant b.
 *   7. `enchainement_mixte_entier_fractions` → Saisie. Entier + 2 fractions à dén multiples.
 *   8. `justifier_somme_figure`           → QCM. Choix d'une justification avec SVG inline.
 *   9. `probleme_reste`                   → QCM, Saisie. Problème contextuel 1 − a/b.
 *
 * Règles dures de la fiche (§2, §3, §4) :
 *   - Pool_A : couples à même dénominateur, b ∈ {5..37}, contraintes a+c ≤ 2b pour +, a>c pour −.
 *   - Pool_B : couples (b,d) où d = k·b, LCM ≤ 36.
 *   - Pool_D : couple (n, a/b), n ∈ [1..12], b ∈ [2..12].
 *   - Pool_E : chaînes à 3 opérandes, au moins un + et un −.
 *
 * Distracteurs contextualisés (fiche §4) :
 *   1. Addition des dénominateurs
 *   2. Oubli d'extension
 *   3. Absorption fautive de l'entier
 *   4. Inversion de signe dans une chaîne
 *   5. Confusion total/reste
 *   6. Oubli de simplification
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem, fmtItemBrut } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 630;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SOM_MEME          = 'Additionner ou soustraire deux fractions de même dénominateur';
const L_SOM_MEME_VF       = 'Juger V/F sur des sommes de fractions de même dénominateur';
const L_SOM_MEME_GUIDEE   = 'Compléter une somme de fractions de même dénominateur (guidée)';
const L_SOM_MULT          = 'Additionner ou soustraire deux fractions à dénominateurs multiples';
const L_SOM_MULT_GUIDEE   = 'Compléter une somme de fractions à dénominateurs multiples (guidée)';
const L_ENT_FRAC          = 'Additionner ou soustraire un entier et une fraction';
const L_ENCH_3            = 'Enchaîner trois fractions de même dénominateur';
const L_ENCH_MIXTE        = 'Enchaîner un entier et deux fractions à dénominateurs multiples';
const L_JUSTIFIER_FIG     = 'Justifier une somme de fractions par une figure d\'aire';
const L_PROB_RESTE        = 'Résoudre un problème de reste à partir d\'une fraction';

// Liste fixe de prénoms (règle projet)
const PRENOMS = [
    'Adam', 'Amélie', 'Chloé', 'Hugo', 'Inès', 'Jade',
    'Karim', 'Léo', 'Lucas', 'Manon', 'Sofia', 'Thomas', 'Yanis',
];

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Formate le résultat simplifié pour affichage interactif avec délimiteurs $...$ KaTeX. */
const fmtItemResult = (a, b) => {
    const s = simplify(a, b);
    return fmtItem(s.num, s.den);
};

/** Construit un elements_additionnels avec une expression KaTeX pour l'énoncé. */
const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

/** Teste si une fraction simplifiée représente un entier (dén=1). */
const isInteger = (a, b) => (simplify(a, b).den === 1);

/**
 * Formate le résultat d'un calcul sous forme canonique :
 *  - si le résultat simplifié est un entier, renvoie "n"
 *  - sinon renvoie "num/den"
 */
const fmtResult = (a, b) => {
    const s = simplify(a, b);
    return fmtFrac(s.num, s.den);
};

// ═══════════════════════════════════════════════════════════
// POOL_A — Couples à même dénominateur
// ═══════════════════════════════════════════════════════════

/**
 * POOL_A — clé = dénominateur b, valeur = liste de numérateurs disponibles.
 * Respecte la fiche §3 sous-famille A (b ∈ {5..37}, on exclut b=2 et b=3
 * pour éviter la trivialité).
 */
const POOL_A = {
    5:  [1, 2, 3, 4, 6, 7, 8, 9],
    6:  [1, 2, 3, 4, 5, 7, 8, 10, 11],
    7:  [1, 2, 3, 4, 5, 6, 8, 9, 10],
    8:  [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13],
    9:  [1, 2, 3, 4, 5, 6, 7, 8, 10, 11],
    10: [1, 3, 7, 9, 11, 13],
    11: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13],
    12: [1, 5, 7, 11, 13, 17],
    14: [1, 3, 5, 9, 11, 13],
    17: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15],
    19: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    27: [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 13],
    33: [5, 7, 11, 13, 17, 19, 23],
    37: [5, 7, 11, 13, 15, 17, 19, 22, 23],
};

for (const [b, nums] of Object.entries(POOL_A)) {
    const bn = Number(b);
    if (bn < 4 || bn > 40) throw new Error(`POOL_A: dénominateur ${b} hors plage`);
    if (nums.length < 3) throw new Error(`POOL_A: pas assez de numérateurs pour ${b}`);
    if (new Set(nums).size !== nums.length) throw new Error(`POOL_A: doublon dans ${b}`);
    for (const a of nums) {
        if (a < 1 || a >= 2 * bn) throw new Error(`POOL_A: num ${a} hors [1,2b−1] pour b=${bn}`);
    }
}

// ═══════════════════════════════════════════════════════════
// POOL_B — Couples (b,d) où d est multiple strict de b
// ═══════════════════════════════════════════════════════════

const POOL_B = [
    [2, 4], [2, 6], [2, 8], [2, 10],
    [3, 6], [3, 9], [3, 12], [3, 15],
    [4, 8], [4, 12], [4, 16], [4, 24],
    [5, 10], [5, 15], [5, 20], [5, 35],
    [6, 12], [6, 18], [6, 24],
    [7, 14], [7, 21], [7, 35],
    [8, 16], [8, 24],
    [9, 18], [9, 27],
    [10, 20],
    [11, 22],
    [12, 24],
];

for (const [b, d] of POOL_B) {
    if (d <= b) throw new Error(`POOL_B: d=${d} doit être > b=${b}`);
    if (d % b !== 0) throw new Error(`POOL_B: d=${d} doit être multiple de b=${b}`);
    if (d > 36) throw new Error(`POOL_B: d=${d} dépasse 36`);
}

// ═══════════════════════════════════════════════════════════
// POOL_D — Couples (entier n, fraction a/b)
// ═══════════════════════════════════════════════════════════

/** Dénominateurs autorisés pour la fraction dans l'exercice entier+fraction. */
const POOL_D_DEN = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Entiers autorisés. */
const POOL_D_N = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// ═══════════════════════════════════════════════════════════
// POOL égalités canoniques pour justifier_somme_figure
// Chaque entrée : [aLeft, bLeft, aRight, bRight, gridCount] où le grid
// est subdivisé en gridCount parts et respecte la somme canonique.
// ═══════════════════════════════════════════════════════════

const POOL_FIGURE_EQUALITES = [
    { a1: 1, b1: 3, a2: 7, b2: 12, grid: 12 },   // 1/3 + 7/12 = 11/12
    { a1: 1, b1: 2, a2: 1, b2: 4, grid: 4 },     // 1/2 + 1/4 = 3/4
    { a1: 1, b1: 4, a2: 5, b2: 8, grid: 8 },     // 1/4 + 5/8 = 7/8
    { a1: 2, b1: 5, a2: 3, b2: 10, grid: 10 },   // 2/5 + 3/10 = 7/10
    { a1: 1, b1: 2, a2: 3, b2: 8, grid: 8 },     // 1/2 + 3/8 = 7/8
    // Entrées retirées : {1/6,1/3,grid6} et {1/4,1/2,grid4} avaient factor1 = grid/b1 = 1
    // (b1 === grid) → le distracteur « oubli d'extension » y devenait une réponse
    // correcte défendable. Ne garder que des égalités où la fraction rose s'étend.
];

// Vérification runtime des égalités canoniques
for (const e of POOL_FIGURE_EQUALITES) {
    const { a1, b1, a2, b2, grid } = e;
    if (grid % b1 !== 0) throw new Error(`POOL_FIGURE: grid=${grid} pas multiple de b1=${b1}`);
    if (grid % b2 !== 0) throw new Error(`POOL_FIGURE: grid=${grid} pas multiple de b2=${b2}`);
    const c1 = a1 * (grid / b1);
    const c2 = a2 * (grid / b2);
    if (c1 + c2 > grid) throw new Error(`POOL_FIGURE: somme > 1 pour ${a1}/${b1}+${a2}/${b2}`);
}

// ═══════════════════════════════════════════════════════════
// CONTEXTES POUR probleme_reste
// ═══════════════════════════════════════════════════════════

const CONTEXTES_RESTE = [
    { verbe: 'parcouru', intro: 'participe à une course', objet: 'du parcours total' },
    { verbe: 'lu', intro: 'commence un nouveau livre', objet: 'du livre' },
    { verbe: 'mangé', intro: 'prépare un gâteau pour son anniversaire', objet: 'du gâteau' },
    { verbe: 'peint', intro: 'repeint un mur dans sa chambre', objet: 'du mur' },
];

// ═══════════════════════════════════════════════════════════
// SÉLECTEURS DE POOLS
// ═══════════════════════════════════════════════════════════

/** Tire un couple (a/b, c/b) distinct depuis POOL_A. */
const pickPoolA = (op = '+') => {
    const bChoices = Object.keys(POOL_A).map(Number);
    for (let attempt = 0; attempt < 100; attempt++) {
        const b = randomChoice(bChoices);
        const nums = POOL_A[b];
        if (nums.length < 2) continue;
        const [a, c] = shuffle(nums).slice(0, 2);
        if (a === c) continue;
        if (op === '+') {
            // Contrainte : a+c ≤ 2b (résultat borné)
            if (a + c > 2 * b) continue;
            return { a, b, c, op };
        } else {
            // Soustraction : a > c pour rester dans ℚ⁺
            if (a <= c) continue;
            return { a, b, c, op };
        }
    }
    throw new Error('pickPoolA: pas de couple trouvé');
};

/** Tire un triplet (a, c, e) distincts avec b commun, au moins 1 + et 1 −. */
const pickPoolATriplet = () => {
    const bChoices = Object.keys(POOL_A).map(Number);
    for (let attempt = 0; attempt < 200; attempt++) {
        const b = randomChoice(bChoices);
        const nums = POOL_A[b];
        if (nums.length < 3) continue;
        const [a, c, e] = shuffle(nums).slice(0, 3);
        // On fixe le premier signe implicite à '+'.
        // Les deux signes suivants doivent contenir au moins un '−'.
        const signs = [
            ['+', '-'], ['-', '+'], ['-', '-'],
        ];
        const sig = randomChoice(signs);
        const s1 = sig[0], s2 = sig[1];
        // Calcul du résultat (numérateur)
        const num = a + (s1 === '+' ? c : -c) + (s2 === '+' ? e : -e);
        if (num <= 0) continue;
        if (num > 2 * b) continue;
        return { a, b, c, e, s1, s2, num };
    }
    throw new Error('pickPoolATriplet: pas de triplet trouvé');
};

/** Tire un couple (b, d) dans POOL_B. */
const pickPoolB = () => randomChoice(POOL_B);

/** Tire un couple (n, a/b) dans POOL_D pour entier+fraction. */
const pickPoolD = (op = '+') => {
    for (let attempt = 0; attempt < 100; attempt++) {
        const n = randomChoice(POOL_D_N);
        const b = randomChoice(POOL_D_DEN);
        // a ∈ [1, 3b-1], non multiple de b (pour que ce ne soit pas un entier déguisé)
        const aMax = 3 * b - 1;
        const a = randInt(1, aMax);
        if (a % b === 0) continue;
        if (op === '+') return { n, a, b, op };
        // Pour la soustraction n - a/b, on exige n > a/b donc n*b > a
        if (n * b > a) return { n, a, b, op };
    }
    throw new Error('pickPoolD: pas de couple trouvé');
};

/** Tire un triplet (n entier, fraction1, fraction2) pour enchainement_mixte. */
const pickPoolEMixte = () => {
    for (let attempt = 0; attempt < 200; attempt++) {
        const n = randInt(2, 10);
        const [b, d] = pickPoolB();
        // a ∈ [1, b-1] (fraction propre)
        const a = randInt(1, b - 1);
        // c ∈ [1, d-1] (fraction propre)
        const c = randInt(1, d - 1);
        // Deux signes (au moins un '−')
        const signs = [
            ['+', '-'], ['-', '+'], ['-', '-'],
        ];
        const [s1, s2] = randomChoice(signs);
        // Calcul au dénominateur commun d
        const k = d / b;
        const numN = n * d;
        const numA = a * k;
        const numC = c;
        const numResult = numN + (s1 === '+' ? numA : -numA) + (s2 === '+' ? numC : -numC);
        if (numResult <= 0) continue;
        if (numResult > 3 * d) continue;
        return { n, a, b, c, d, k, s1, s2, numResult };
    }
    throw new Error('pickPoolEMixte: pas de triplet trouvé');
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — somme_meme_denominateur (QCM)
// ═══════════════════════════════════════════════════════════

const genSommeMemeDenQcm = () => {
    const op = randomChoice(['+', '-']);
    const { a, b, c } = pickPoolA(op);
    const exprKatex = `\\dfrac{${a}}{${b}} ${op === '+' ? '+' : '-'} \\dfrac{${c}}{${b}} = ?`;
    const numResult = op === '+' ? a + c : a - c;
    const bonne = fmtItemResult(numResult, b);
    // Distracteurs
    const distr1 = fmtItem(numResult, 2 * b);           // addition des dén
    const distr2 = numResult === 0 ? String(2 * b) : fmtItem(numResult * 2, b); // doublement
    const distr3 = fmtItem(op === '+' ? a + c : a - c, b);
    // distr3 est le résultat NON SIMPLIFIÉ (si simplifiable)
    const distr4 = String(numResult); // sans dénominateur
    const choixSet = new Set([bonne, distr1, distr2, distr4]);
    // Assurer 4 choix distincts
    let fallback = 1;
    while (choixSet.size < 4) {
        choixSet.add(fmtItem(numResult + fallback, b));
        fallback++;
    }
    const choix = [...choixSet];
    const texte = `<p>Calcule et donne le résultat sous forme de fraction.</p>{{element:0}}`;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_SOM_MEME), elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'somme_meme_denominateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1bis — somme_meme_denominateur (Saisie)
// ═══════════════════════════════════════════════════════════

const genSommeMemeDenSaisie = () => {
    const op = randomChoice(['+', '-']);
    const { a, b, c } = pickPoolA(op);
    const exprKatex = `\\dfrac{${a}}{${b}} ${op === '+' ? '+' : '-'} \\dfrac{${c}}{${b}} = ?`;
    const numResult = op === '+' ? a + c : a - c;
    const bonne = fmtResult(numResult, b);
    const texte = `<p>Calcule et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_SOM_MEME), strict: false, elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'somme_meme_denominateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1ter — somme_meme_denominateur (Vrai/Faux)
// ═══════════════════════════════════════════════════════════

const genSommeMemeDenVf = () => {
    // 6 lignes avec alternance V/F, on pioche 6 couples distincts
    const seen = new Set();
    const lignes = [];
    const bonnesReponses = [];
    let attempts = 0;
    while (lignes.length < 6 && attempts < 100) {
        attempts++;
        const op = randomChoice(['+', '-']);
        const { a, b, c } = pickPoolA(op);
        const numResult = op === '+' ? a + c : a - c;
        if (numResult <= 0) continue;
        const correct = fmtResult(numResult, b);
        const correctKatex = (() => { const s = simplify(numResult, b); return fmtKatex(s.num, s.den); })();
        // Alternance : pair = V (donne le bon résultat), impair = F (faux distracteur)
        const faire_vrai = lignes.length % 2 === 0;
        let resKatex;
        if (faire_vrai) {
            resKatex = correctKatex;
        } else {
            // Distracteur : addition des dénominateurs
            const distrPlain = fmtFrac(numResult, 2 * b);
            if (distrPlain === correct) continue; // pas d'ambigu
            resKatex = fmtKatex(numResult, 2 * b);
        }
        const key = `${a}/${b}${op}${c}/${b}=${resKatex}`;
        if (seen.has(key)) continue;
        seen.add(key);
        lignes.push(`$${fmtKatex(a, b)} ${op} ${fmtKatex(c, b)} = ${resKatex}$`);
        bonnesReponses.push(faire_vrai ? 0 : 1);
    }
    const texte = `<p>Pour chaque ligne, indique si l'égalité est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_SOM_MEME_VF));
    payload._meta.generatorKey = 'somme_meme_denominateur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — somme_meme_denominateur_guidee (texte à trous)
// ═══════════════════════════════════════════════════════════

const genSommeMemeDenGuidee = () => {
    // On pioche 3 numérateurs distincts pour une chaîne additive simple
    // (c'est le format "ex OA35-1" : 3/14 + 1/14 + 5/14)
    const bChoices = Object.keys(POOL_A).map(Number);
    let tryCount = 0;
    while (tryCount < 50) {
        tryCount++;
        const b = randomChoice(bChoices);
        const nums = POOL_A[b];
        if (nums.length < 3) continue;
        const [a, c, e] = shuffle(nums).slice(0, 3);
        const sum = a + c + e;
        if (sum > 2 * b) continue;
        const phrase = `$\\dfrac{${a}}{${b}} + \\dfrac{${c}}{${b}} + \\dfrac{${e}}{${b}} = \\dfrac{{{${a}}} + {{${c}}} + {{${e}}}}{${b}} = \\dfrac{{{${sum}}}}{${b}}$`;
        const texte = `<p>Complète le calcul en additionnant les numérateurs. Le dénominateur commun ${b} ne change pas.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_SOM_MEME_GUIDEE), mode: 'saisie', strict: false });
        payload._meta.generatorKey = 'somme_meme_denominateur_guidee';
        return payload;
    }
    throw new Error('genSommeMemeDenGuidee: pas de triplet trouvé');
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — somme_denominateurs_multiples (QCM)
// ═══════════════════════════════════════════════════════════

const genSommeDenMultiplesQcm = () => {
    const op = randomChoice(['+', '-']);
    for (let attempt = 0; attempt < 100; attempt++) {
        const [b, d] = pickPoolB();
        const k = d / b;
        // a ∈ [1, b-1] pour éviter fraction impropre côté gauche
        const a = randInt(1, b - 1);
        // c ∈ [1, d-1]
        const c = randInt(1, d - 1);
        // Numérateur étendu
        const aExtended = a * k;
        if (aExtended === c) continue; // évite la trivialité 2/6 + 2/6
        const numResult = op === '+' ? aExtended + c : aExtended - c;
        if (numResult <= 0) continue;
        if (numResult > 2 * d) continue;
        const bonne = fmtItemResult(numResult, d);
        // Distracteurs ciblés (fiche §4)
        const distr_addEtages = fmtItem(a + c, b + d);         // addition des 2 étages
        const distr_oubliExt = fmtItem(a + c, d);              // oubli d'extension du numérateur
        const distr_extTout = fmtItem(aExtended + c, b * d);   // extension correcte num mais mauvais dén
        const choixSet = new Set([bonne, distr_addEtages, distr_oubliExt, distr_extTout]);
        let fallback = 1;
        while (choixSet.size < 4) {
            choixSet.add(fmtItem(numResult + fallback, d));
            fallback++;
        }
        const choix = [...choixSet];
        const exprKatex = `\\dfrac{${a}}{${b}} ${op === '+' ? '+' : '-'} \\dfrac{${c}}{${d}} = ?`;
        const texte = `<p>Effectue l'opération et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
        const payload = makeQcm(choix, bonne, texte, { ...opts(L_SOM_MULT), elements_additionnels: exprElement(exprKatex) });
        payload._meta.generatorKey = 'somme_denominateurs_multiples';
        return payload;
    }
    throw new Error('genSommeDenMultiplesQcm: pas de couple trouvé');
};

const genSommeDenMultiplesSaisie = () => {
    const op = randomChoice(['+', '-']);
    for (let attempt = 0; attempt < 100; attempt++) {
        const [b, d] = pickPoolB();
        const k = d / b;
        const a = randInt(1, b - 1);
        const c = randInt(1, d - 1);
        const aExtended = a * k;
        if (aExtended === c) continue;
        const numResult = op === '+' ? aExtended + c : aExtended - c;
        if (numResult <= 0) continue;
        if (numResult > 2 * d) continue;
        const bonne = fmtResult(numResult, d);
        const exprKatex = `\\dfrac{${a}}{${b}} ${op === '+' ? '+' : '-'} \\dfrac{${c}}{${d}} = ?`;
        const texte = `<p>Effectue l'opération et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
        const payload = makeSaisie(bonne, texte, { ...opts(L_SOM_MULT), strict: false, elements_additionnels: exprElement(exprKatex) });
        payload._meta.generatorKey = 'somme_denominateurs_multiples';
        return payload;
    }
    throw new Error('genSommeDenMultiplesSaisie: pas de couple trouvé');
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — somme_denominateurs_multiples_guidee
// ═══════════════════════════════════════════════════════════

const genSommeDenMultiplesGuidee = () => {
    for (let attempt = 0; attempt < 100; attempt++) {
        const [b, d] = pickPoolB();
        const k = d / b;
        const a = randInt(1, b - 1);
        const c = randInt(1, d - 1);
        const aExtended = a * k;
        if (aExtended === c) continue;
        const numResult = aExtended + c;
        if (numResult <= 0 || numResult > 2 * d) continue;
        const phrase = `$\\dfrac{${a}}{${b}} = \\dfrac{${a} \\times {{${k}}}}{${b} \\times {{${k}}}} = \\dfrac{{{${aExtended}}}}{${d}}$\n$\\dfrac{${a}}{${b}} + \\dfrac{${c}}{${d}} = \\dfrac{{{${aExtended}}}}{${d}} + \\dfrac{${c}}{${d}} = \\dfrac{{{${numResult}}}}{${d}}$`;
        const texte = `<p>Réduis au même dénominateur puis calcule. Complète chaque étape.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_SOM_MULT_GUIDEE), mode: 'saisie', strict: false });
        payload._meta.generatorKey = 'somme_denominateurs_multiples_guidee';
        return payload;
    }
    throw new Error('genSommeDenMultiplesGuidee: pas de couple trouvé');
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — entier_plus_ou_moins_fraction
// ═══════════════════════════════════════════════════════════

const genEntierPlusFractionQcm = () => {
    const op = randomChoice(['+', '-']);
    const { n, a, b } = pickPoolD(op);
    const nExtended = n * b;  // conversion de n en b-ième
    const numResult = op === '+' ? nExtended + a : nExtended - a;
    const bonne = fmtItemResult(numResult, b);
    // Distracteurs
    const distr_absorption = fmtItem(op === '+' ? n + a : Math.abs(n - a), b); // absorption n dans num
    const distr_sansEntier = fmtItem(a, b);                                     // oubli de l'entier
    const distr_extDeuxEtages = fmtItem(numResult, nExtended);                  // extension aux 2 étages
    const choixSet = new Set([bonne, distr_absorption, distr_sansEntier, distr_extDeuxEtages]);
    let fallback = 1;
    while (choixSet.size < 4) {
        choixSet.add(fmtItem(numResult + fallback, b));
        fallback++;
    }
    const choix = [...choixSet];
    const exprKatex = `${n} ${op === '+' ? '+' : '-'} \\dfrac{${a}}{${b}} = ?`;
    const texte = `<p>Effectue l'opération et donne le résultat sous forme de fraction.</p>{{element:0}}`;
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_ENT_FRAC), elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'entier_plus_ou_moins_fraction';
    return payload;
};

const genEntierPlusFractionSaisie = () => {
    const op = randomChoice(['+', '-']);
    const { n, a, b } = pickPoolD(op);
    const nExtended = n * b;
    const numResult = op === '+' ? nExtended + a : nExtended - a;
    const bonne = fmtResult(numResult, b);
    const exprKatex = `${n} ${op === '+' ? '+' : '-'} \\dfrac{${a}}{${b}} = ?`;
    const texte = `<p>Effectue l'opération et donne le résultat sous forme de fraction.</p>{{element:0}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_ENT_FRAC), strict: false, elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'entier_plus_ou_moins_fraction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — enchainement_trois_operandes (Saisie)
// ═══════════════════════════════════════════════════════════

const genEnchainementTroisOperandes = () => {
    const { a, b, c, e, s1, s2, num } = pickPoolATriplet();
    const bonne = fmtResult(num, b);
    const exprKatex = `\\dfrac{${a}}{${b}} ${s1} \\dfrac{${c}}{${b}} ${s2} \\dfrac{${e}}{${b}} = ?`;
    const texte = `<p>Effectue la chaîne de calculs et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_ENCH_3), strict: false, elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'enchainement_trois_operandes';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — enchainement_mixte_entier_fractions (Saisie)
// ═══════════════════════════════════════════════════════════

const genEnchainementMixte = () => {
    const { n, a, b, c, d, s1, s2, numResult } = pickPoolEMixte();
    const bonne = fmtResult(numResult, d);
    const exprKatex = `${n} ${s1} \\dfrac{${a}}{${b}} ${s2} \\dfrac{${c}}{${d}} = ?`;
    const texte = `<p>Effectue la chaîne de calculs et donne le résultat sous forme de fraction la plus simple.</p>{{element:0}}`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_ENCH_MIXTE), strict: false, elements_additionnels: exprElement(exprKatex) });
    payload._meta.generatorKey = 'enchainement_mixte_entier_fractions';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — justifier_somme_figure (QCM avec SVG)
// ═══════════════════════════════════════════════════════════

// Couleurs claires pour le dark-mode (bg-slate-800/50 dans ImageElementEleve)
const SVG_STROKE = 'currentColor';

/** Helper : construit les elements_additionnels avec une image SVG data URL. */
const svgImageElement = (dataUrl, maxWidth = 'compact') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: dataUrl, maxWidth } }],
});

/** Construit un data URL SVG d'une bande divisée en `grid` parts, avec c1 parts roses à gauche puis c2 parts bleues. */
const buildFigureSvg = (grid, c1, c2) => {
    const W = 360, H = 120;
    const pad = 10;
    const cellW = (W - 2 * pad) / grid;
    const y1 = 20, h = 80;
    const parts = [];
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`);
    parts.push(`<rect x="${pad}" y="${y1}" width="${W - 2 * pad}" height="${h}" fill="rgba(255,255,255,0.1)" stroke="${SVG_STROKE}" stroke-width="2"/>`);
    for (let i = 0; i < grid; i++) {
        const x = pad + i * cellW;
        let fill = 'rgba(255,255,255,0.1)';
        if (i < c1) fill = '#fb7185'; // rose (visible sur fond sombre)
        else if (i < c1 + c2) fill = '#3b82f6'; // bleu (visible sur fond sombre)
        parts.push(`<rect x="${x.toFixed(2)}" y="${y1}" width="${cellW.toFixed(2)}" height="${h}" fill="${fill}"/>`);
    }
    // Séparations verticales
    for (let i = 1; i < grid; i++) {
        const x = pad + i * cellW;
        parts.push(`<line x1="${x.toFixed(2)}" y1="${y1}" x2="${x.toFixed(2)}" y2="${y1 + h}" stroke="${SVG_STROKE}" stroke-width="1"/>`);
    }
    parts.push(`</svg>`);
    const svg = parts.join('');
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** PGCD récursif (pour LCM). */
const gcdInt = (a, b) => b === 0 ? a : gcdInt(b, a % b);
const lcmInt = (a, b) => (a * b) / gcdInt(a, b);

/**
 * Génère dynamiquement une égalité {a1, b1, a2, b2, grid} valide :
 * - b1 et b2 | grid (grille commune = LCM)
 * - grid ≤ 12 (figure lisible)
 * - a1 < b1, a2 < b2 (fractions propres)
 * - c1 + c2 ≤ grid (somme ≤ 1)
 */
const genFigureEqualiteDynamique = () => {
    const denoms = [2, 3, 4, 5, 6, 8, 10, 12];
    for (let attempts = 0; attempts < 50; attempts++) {
        const b1 = randomChoice(denoms);
        const b2 = randomChoice(denoms);
        const grid = lcmInt(b1, b2);
        if (grid > 12) continue;
        const factor1 = grid / b1;
        // La fraction rose DOIT nécessiter une extension (b1 < grid) : sinon c1 === a1
        // et le distracteur « oubli d'extension » (a1 + c2) redonne la bonne réponse
        // avec un raisonnement valide → deux justifications correctes.
        if (factor1 === 1) continue;
        const factor2 = grid / b2;
        const a1 = randInt(1, b1 - 1);
        const c1 = a1 * factor1;
        const maxA2 = Math.min(b2 - 1, Math.floor((grid - c1) / factor2));
        if (maxA2 < 1) continue;
        const a2 = randInt(1, maxA2);
        return { a1, b1, a2, b2, grid };
    }
    return null;
};

const genJustifierSommeFigure = () => {
    // Génération dynamique : ~500 combinaisons possibles, fallback pool curé
    const eq = genFigureEqualiteDynamique() || randomChoice(POOL_FIGURE_EQUALITES);
    const { a1, b1, a2, b2, grid } = eq;
    const c1 = a1 * (grid / b1);
    const c2 = a2 * (grid / b2);
    const totalColor = c1 + c2;
    const simplifiedResult = simplify(totalColor, grid);
    // Helpers KaTeX pour les fractions dans les choix
    const f1 = fmtItem(a1, b1), f2 = fmtItem(a2, b2);
    // Forme BRUTE (retouche 2026-07-28) : fmtItem simplifiait déjà fRes, rendant
    // la queue « = fSimpItem » morte et la phrase incohérente (« 6 parts sur 12,
    // donc … = 1/2 » sans montrer 6/12). En brut, l'étape pédagogique revit.
    const fRes = fmtItemBrut(totalColor, grid);
    const fSimpItem = fmtItem(simplifiedResult.num, simplifiedResult.den);
    // Helper pour accord singulier/pluriel : "1 part rose" / "3 parts roses"
    const partsFm = (n, adj) => `${n} part${n > 1 ? 's' : ''} ${adj}${n > 1 ? 's' : ''}`;
    // Justification correcte (A)
    const bonne = `On partage en ${grid} parts égales : ${f1} vaut ${partsFm(c1, 'rose')}, ${f2} vaut ${partsFm(c2, 'bleue')} ; au total ${c1} + ${c2} = ${totalColor} parts sur ${grid}, donc ${f1} + ${f2} = ${fRes}${(simplifiedResult.den !== grid && fRes !== fSimpItem) ? ` = ${fSimpItem}` : ''}.`;
    // Distracteur B : addition des deux étages
    const badB_num = a1 + a2;
    const badB_den = b1 + b2;
    const distr_addEtages = `On additionne les numérateurs et les dénominateurs : ${a1} + ${a2} = ${badB_num} et ${b1} + ${b2} = ${badB_den}, donc ${f1} + ${f2} = ${fmtItem(badB_num, badB_den)}.`;
    // Distracteur C : compte brut avec mauvais dénominateur
    const fakeTotal = grid + 3; // faux dénominateur apparent
    const distr_compteBrut = `On compte les parts colorées : il y a ${totalColor} parts en tout, la bande fait ${fakeTotal} parts, donc ${f1} + ${f2} = ${fmtItem(totalColor, fakeTotal)}.`;
    // Distracteur D : oubli d'extension (on prend a1 brut au lieu de c1)
    const distr_oubliExt = `On ajoute ${partsFm(a1, 'rose')} et ${partsFm(c2, 'bleue')} : ${a1} + ${c2} = ${a1 + c2}, sur ${grid} parts, donc ${f1} + ${f2} = ${fmtItem(a1 + c2, grid)}.`;
    const choix = [bonne, distr_addEtages, distr_compteBrut, distr_oubliExt];
    const elAdd = svgImageElement(buildFigureSvg(grid, c1, c2));
    const texte = `<p>La figure ci-dessous illustre le calcul {{element:1}}. Quelle justification donne le bon résultat\u00a0?</p>{{element:0}}`;
    elAdd.elements.push({ type: 'expression', config: { contenu: `${fmtKatex(a1, b1)} + ${fmtKatex(a2, b2)}` } });
    const payload = makeQcm(choix, bonne, texte, { ...opts(L_JUSTIFIER_FIG), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'justifier_somme_figure';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 9 — probleme_reste (QCM et Saisie)
// ═══════════════════════════════════════════════════════════

const genProblemeResteBase = () => {
    const prenom = randomChoice(PRENOMS);
    const ctx = randomChoice(CONTEXTES_RESTE);
    // Fraction parcourue a/b avec 0 < a < b, b ∈ [3, 12]
    const b = randInt(3, 12);
    const a = randInt(1, b - 1);
    const resteNum = b - a;
    const simp = simplify(resteNum, b);
    const bonne = fmtFrac(simp.num, simp.den);
    // Fraction en KaTeX dans l'énoncé, telle qu'écrite (audit fractions en
    // texte, 2026-09-06) ; la réponse tapée reste « 5/8 ».
    const fracDisplay = fmtItemBrut(a, b);
    const enonce = `${prenom} ${ctx.intro}. ${prenom} a ${ctx.verbe} ${fracDisplay} ${ctx.objet}. Quelle fraction ${ctx.objet} lui reste-t-il ?`;
    return { enonce, bonne, a, b, resteNum };
};

const genProblemeResteQcm = () => {
    const { enonce, a, b, resteNum } = genProblemeResteBase();
    const simp = simplify(resteNum, b);
    const bonne = fmtItem(simp.num, simp.den);
    // Distracteurs
    const distr_meme = fmtItem(a, b);                          // confusion reste/part faite
    const distr_inverse = fmtItem(b, a);                       // inversion num/dén
    const distr_numSeul = fmtItem(1, b);                       // complément sur num seulement
    const choixSet = new Set([bonne, distr_meme, distr_inverse, distr_numSeul]);
    let fallback = 1;
    while (choixSet.size < 4) {
        choixSet.add(fmtItem(resteNum + fallback, b));
        fallback++;
    }
    const choix = [...choixSet];
    const texte = `<p>${enonce}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_PROB_RESTE));
    payload._meta.generatorKey = 'probleme_reste';
    return payload;
};

const genProblemeResteSaisie = () => {
    const { enonce, bonne } = genProblemeResteBase();
    const texte = `<p>${enonce}</p>`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_PROB_RESTE), strict: false });
    payload._meta.generatorKey = 'probleme_reste';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY DES GENERATEURS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    somme_meme_denominateur_qcm_unique:           genSommeMemeDenQcm,
    somme_meme_denominateur_texte:                genSommeMemeDenSaisie,
    somme_meme_denominateur_tableau_vf:           genSommeMemeDenVf,
    somme_meme_denominateur_guidee_texte_trou_saisie: genSommeMemeDenGuidee,
    somme_denominateurs_multiples_qcm_unique:     genSommeDenMultiplesQcm,
    somme_denominateurs_multiples_texte:          genSommeDenMultiplesSaisie,
    somme_denominateurs_multiples_guidee_texte_trou_saisie: genSommeDenMultiplesGuidee,
    entier_plus_ou_moins_fraction_qcm_unique:     genEntierPlusFractionQcm,
    entier_plus_ou_moins_fraction_texte:          genEntierPlusFractionSaisie,
    enchainement_trois_operandes_texte:           genEnchainementTroisOperandes,
    enchainement_mixte_entier_fractions_texte:    genEnchainementMixte,
    justifier_somme_figure_qcm_unique:            genJustifierSommeFigure,
    probleme_reste_qcm_unique:                    genProblemeResteQcm,
    probleme_reste_texte:                         genProblemeResteSaisie,
};

// ═══════════════════════════════════════════════════════════
// CATEGORIES (pour l'UI prof)
// ═══════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi',
    texte: 'Saisie',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
    ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k === prefix || k.startsWith(`${prefix}_`))
        .map(k => {
            const suffix = k.slice(prefix.length + 1);
            return { key: k, label: FORMAT_LABELS[suffix] || suffix || 'Standard' };
        });

export const CATEGORIES = [
    { id: 'somme_meme_denominateur',          label: 'Somme : même dénominateur',           formatsList: buildFormats('somme_meme_denominateur') },
    { id: 'somme_meme_denominateur_guidee',   label: 'Somme : même dén. (guidée)',          formatsList: buildFormats('somme_meme_denominateur_guidee') },
    { id: 'somme_denominateurs_multiples',    label: 'Somme : dén. multiples',              formatsList: buildFormats('somme_denominateurs_multiples') },
    { id: 'somme_denominateurs_multiples_guidee', label: 'Somme : dén. multiples (guidée)', formatsList: buildFormats('somme_denominateurs_multiples_guidee') },
    { id: 'entier_plus_ou_moins_fraction',    label: 'Entier ± fraction',                   formatsList: buildFormats('entier_plus_ou_moins_fraction') },
    { id: 'enchainement_trois_operandes',     label: 'Enchaînement à 3 fractions',          formatsList: buildFormats('enchainement_trois_operandes') },
    { id: 'enchainement_mixte_entier_fractions', label: 'Enchaînement entier + fractions',  formatsList: buildFormats('enchainement_mixte_entier_fractions') },
    { id: 'justifier_somme_figure',           label: 'Justifier une somme (figure)',        formatsList: buildFormats('justifier_somme_figure') },
    { id: 'probleme_reste',                   label: 'Problème de reste',                   formatsList: buildFormats('probleme_reste') },
];

// Le registry doit attacher generatorKey distinct pour l'exercice
// sans remplacer celui que nos fonctions posent déjà.
// Certains suffixes sont eux-mêmes inclus dans d'autres clés (ex : somme_meme_denominateur
// est un préfixe de somme_meme_denominateur_guidee), la fonction buildFormats n'est pas
// sensible à ça parce qu'on filtre sur k === prefix OR k.startsWith(prefix + '_'), mais
// somme_meme_denominateur_guidee_texte_trou_saisie commence par somme_meme_denominateur_
// et risque d'être inclus. On prend la précaution d'exclure ici les clés qui appartiennent
// à une sous-catégorie plus spécifique.
for (const cat of CATEGORIES) {
    cat.formatsList = cat.formatsList.filter(f => {
        // Si un autre category id est un préfixe plus long de f.key, on l'exclut de la présente
        for (const other of CATEGORIES) {
            if (other.id === cat.id) continue;
            if (other.id.startsWith(cat.id + '_') && (f.key === other.id || f.key.startsWith(other.id + '_'))) {
                return false;
            }
        }
        return true;
    });
}

// ═══════════════════════════════════════════════════════════
// ENTRÉE PRINCIPALE
// ═══════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};

// Exports internes pour les tests
export const _internals = {
    POOL_A, POOL_B, POOL_D_DEN, POOL_D_N, POOL_FIGURE_EQUALITES, CONTEXTES_RESTE, PRENOMS,
    GENERATORS, CATEGORIES,
    gcd, simplify, fmtFrac, fmtKatex, fmtItem, fmtItemResult, fmtResult, isInteger,
    pickPoolA, pickPoolATriplet, pickPoolB, pickPoolD, pickPoolEMixte,
    buildFigureSvg,
    genSommeMemeDenQcm, genSommeMemeDenSaisie, genSommeMemeDenVf, genSommeMemeDenGuidee,
    genSommeDenMultiplesQcm, genSommeDenMultiplesSaisie, genSommeDenMultiplesGuidee,
    genEntierPlusFractionQcm, genEntierPlusFractionSaisie,
    genEnchainementTroisOperandes, genEnchainementMixte,
    genJustifierSommeFigure,
    genProblemeResteQcm, genProblemeResteSaisie,
};
